import {
  MainButton,
  ModalConfirmAction,
  STATUSPAGE,
  Select,
  convertEventSelectToTarget,
} from '@repo/ui';
import { GetAllQuestionsUseCaseInterface } from 'domain/usecases';
import { useFormik } from 'formik';
import { NumericDecimal } from 'presentation/app/components/inputs/numeric-decimal';
import { QUESTION_TYPE_OPTIONS } from 'presentation/app/modules/EvaluationCriteria/common/question-types';
import { ModalQuestionsForm } from 'presentation/app/modules/EvaluationCriteria/components/modals/modal-questions-form';
import { OverlineTitle } from 'presentation/app/modules/EvaluationCriteria/components/overline-title';
import { TableDraggableEvaluationCriteria } from 'presentation/app/modules/EvaluationCriteria/components/table-draggable-evaluation-criteria';
import { useQuestions } from 'presentation/app/modules/EvaluationCriteria/hooks/useQuestions';
import React, { useState } from 'react';
import { Col, Row, Form, Card, Label, Input, CardBody } from 'reactstrap';
import * as Yup from 'yup';

interface IQuestionsSectionProps {
  id: string;
  questions: GetAllQuestionsUseCaseInterface.Result;
}

export const QuestionsSection = ({ id, questions }: IQuestionsSectionProps) => {
  const [isModalEditOpen, setIsModalEditOpen] = useState<boolean>(false);
  const [isOpenModalCopy, setIsOpenModalCopy] = useState<boolean>(false);
  const [questionIdToCopy, setQuestionIdToCopy] = useState<string>('');

  const {
    cloneStatus,
    requestCopyQuestion,
    createStatus,
    requestDeleteQuestion,
    requestCreateQuestion,
    requestGetQuestionById,
    requestReorderQuestions,
    areInputsAndBtnsDisabled,
    getByIdStatus,
  } = useQuestions();

  function toggleModalEdit() {
    setIsModalEditOpen(!isModalEditOpen);
  }

  function toggleModalCopyQuestion(id?: string) {
    setQuestionIdToCopy(id);
    setIsOpenModalCopy(!isOpenModalCopy);
  }

  const handleEdit = (id: number) => {
    requestGetQuestionById({ id });
    toggleModalEdit();
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: '',
      type: null,
      weight: '',
      maxScore: '',
      required: true,
      instructions: '',
    },
    validationSchema: Yup.object({
      title: Yup.string().required('Campo obrigatório'),
      type: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .required('Campo obrigatório'),
      maxScore: Yup.string().nullable(),
      weight: Yup.string().nullable(),
      required: Yup.boolean().nullable(),
      instructions: Yup.string().nullable(),
    }),
    onSubmit: (values) => {
      const valuesToSumit = {
        criteriaId: id,
        title: values.title,
        type: values.type?.value,
        required: values.required,
        weightage: values.weight === '' ? null : Number(values.weight),
        maxScore: values.maxScore === '' ? null : Number(values.maxScore),
        instruction: values.instructions || null,
      };

      requestCreateQuestion(valuesToSumit);
      validation.resetForm();
    },
  });

  return (
    <React.Fragment>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <Card>
          <OverlineTitle title="Questões" />

          <CardBody className="p-4 pb-0">
            <Row className="g-4">
              <Col xs={12} md={6} lg={6}>
                <Label className="form-label" htmlFor="title">
                  Título
                </Label>
                <Input
                  id="title"
                  name="title"
                  onBlur={validation.handleBlur}
                  value={validation.values.title}
                  onChange={validation.handleChange}
                  invalid={
                    !!(validation.touched.title && validation.errors.title)
                  }
                />
              </Col>

              <Col xs={12} md={2} lg={2}>
                <Label className="form-label" htmlFor="type">
                  Tipo
                </Label>
                <Select
                  isClearable
                  id="type"
                  name="type"
                  placeholder="Selecione um tipo"
                  onBlur={validation.handleBlur}
                  value={validation?.values?.type}
                  options={[...QUESTION_TYPE_OPTIONS]}
                  onChange={(e) => {
                    validation.handleChange(
                      convertEventSelectToTarget('type', e),
                    );
                  }}
                  invalid={
                    !!(validation?.touched?.type && validation?.errors?.type)
                  }
                />
              </Col>

              <Col xs={12} md={2} lg={2}>
                <Label className="form-label" htmlFor="weight">
                  Peso
                </Label>
                <NumericDecimal
                  id="weight"
                  name="weight"
                  allowNegative={false}
                  value={validation.values.weight}
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                />
              </Col>

              <Col xs={12} md={2} lg={2}>
                <Label className="form-label" htmlFor="maxScore">
                  Nota Máxima
                </Label>
                <NumericDecimal
                  id="maxScore"
                  name="maxScore"
                  allowNegative={false}
                  value={validation.values.maxScore}
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  isAllowed={(vals) => {
                    if (vals.floatValue === undefined) return true;
                    return vals.floatValue < 1000;
                  }}
                />
              </Col>

              <Col
                xs={12}
                md={2}
                lg={2}
                style={{
                  maxHeight: 70,
                  display: 'grid',
                  alignItems: 'end',
                }}
              >
                <div className="form-check">
                  <Input
                    id="required"
                    type="checkbox"
                    name="required"
                    className="form-check-input"
                    onBlur={validation.handleBlur}
                    onChange={validation.handleChange}
                    checked={validation.values.required}
                  />
                  <Label htmlFor="required" className="form-label">
                    Resposta Obrigatória
                  </Label>
                </div>
              </Col>

              <Col md={12}>
                <Label htmlFor="instructions" className="form-label">
                  Instrução
                </Label>
                <Input
                  rows={4}
                  id="instructions"
                  type="textarea"
                  name="instructions"
                  className="form-control"
                  onBlur={validation.handleBlur}
                  value={validation.values?.instructions}
                  onChange={validation.handleChange}
                />
              </Col>

              <Col md={12} className="d-flex justify-content-end mt-3">
                <MainButton
                  type="submit"
                  isDisabled={areInputsAndBtnsDisabled}
                  isLoading={createStatus === STATUSPAGE.LOADING}
                >
                  <i className="ri-add-line"></i>
                  <span className="me-2">Adicionar</span>
                </MainButton>
              </Col>
            </Row>

            <Row className="mt-3">
              {questions.length > 0 && (
                <TableDraggableEvaluationCriteria
                  requestType="questions"
                  requestReorder={requestReorderQuestions}
                  handleDelete={(id) => requestDeleteQuestion({ id })}
                  handleEdit={handleEdit}
                  data={questions || []}
                  columns={[
                    {
                      header: 'Título',
                      key: 'title',
                      render: (row) => (
                        <div>
                          <p className="fw-semibold m-0">{row.title}</p>
                          {row.instruction && (
                            <p className="text-muted m-0 fw-medium">
                              <i className="ri-information-line me-2 text-info"></i>
                              <em>{row.instruction}</em>
                            </p>
                          )}
                        </div>
                      ),
                    },
                    { header: 'Tipo', key: 'typeName' },
                    { header: 'Peso', key: 'weightage' },
                    {
                      header: 'Nota Máxima',
                      key: 'maxScore',
                      render: (row) => (
                        <span>
                          {row.maxScore !== null && row.maxScore !== undefined
                            ? row.maxScore
                            : '-'}
                        </span>
                      ),
                    },
                    {
                      header: 'Obrigatório',
                      key: 'required',
                      render: (row) => (
                        <i
                          className={`ri-${
                            row.required ? 'checkbox' : 'close'
                          }-circle-line fs-18 text-${
                            row.required ? 'success' : 'danger'
                          }`}
                        ></i>
                      ),
                    },
                  ]}
                  actions={[
                    {
                      label: 'Copiar',
                      icon: 'copy',
                      color: 'primary',
                      onClick: (row) => toggleModalCopyQuestion(String(row.id)),
                    },
                  ]}
                />
              )}
            </Row>
          </CardBody>
        </Card>

        <ModalQuestionsForm
          id={id}
          isOpen={isModalEditOpen}
          toggle={toggleModalEdit}
          loadingQuestion={getByIdStatus === STATUSPAGE.LOADING}
        />
        <ModalConfirmAction
          loading={cloneStatus === STATUSPAGE.LOADING}
          title="Copiar Questão?"
          subtitle="Confirma a cópia desta questão?"
          isOpen={isOpenModalCopy}
          toggle={() => {
            toggleModalCopyQuestion();
          }}
          onConfirm={() => {
            requestCopyQuestion(
              { questionId: questionIdToCopy },
              toggleModalCopyQuestion,
              handleEdit,
            );
          }}
          modalTheme="primary"
          actionBtnLabel="Sim"
          cancelBtnLabel="Não"
          featherIconName="help-circle"
        />
      </Form>
    </React.Fragment>
  );
};
